import { create } from 'zustand'

interface EditorState {
    activeTool: string
    selectedObjectIds: string[]
    zoom: number
    currentPageIndex: number
    undoStack: string[]
    redoStack: string[]
    isSaving: boolean
    isDirty: boolean
}

interface EditorActions {
    setActiveTool: (tool: string) => void
    setSelectedObjects: (ids: string[]) => void
    setZoom: (zoom: number) => void
    setCurrentPageIndex: (index: number) => void
    pushUndo: (canvasJson: string) => void
    undo: () => string | undefined
    redo: () => string | undefined
    setIsSaving: (saving: boolean) => void
    setIsDirty: (dirty: boolean) => void
    resetEditor: () => void
}

type EditorStore = EditorState & EditorActions

const MAX_UNDO_STACK = 50

const initialState: EditorState = {
    activeTool: 'select',
    selectedObjectIds: [],
    zoom: 1,
    currentPageIndex: 0,
    undoStack: [],
    redoStack: [],
    isSaving: false,
    isDirty: false,
}

export const useEditorStore = create<EditorStore>()((set, get) => ({
    ...initialState,

    setActiveTool: (tool) => set({ activeTool: tool }),

    setSelectedObjects: (ids) => set({ selectedObjectIds: ids }),

    setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(5, zoom)) }),

    setCurrentPageIndex: (index) => set({ currentPageIndex: index }),

    pushUndo: (canvasJson) => {
        const { undoStack } = get()
        const newStack = [...undoStack, canvasJson]
        if (newStack.length > MAX_UNDO_STACK) {
            newStack.shift()
        }
        set({ undoStack: newStack, redoStack: [], isDirty: true })
    },

    undo: () => {
        const { undoStack, redoStack } = get()
        if (undoStack.length === 0) return undefined
        const current = undoStack[undoStack.length - 1]
        const previous = undoStack[undoStack.length - 2]
        set({
            undoStack: undoStack.slice(0, -1),
            redoStack: [...redoStack, current],
        })
        return previous
    },

    redo: () => {
        const { redoStack } = get()
        if (redoStack.length === 0) return undefined
        const next = redoStack[redoStack.length - 1]
        set({
            redoStack: redoStack.slice(0, -1),
            undoStack: [...get().undoStack, next],
        })
        return next
    },

    setIsSaving: (saving) => set({ isSaving: saving }),
    setIsDirty: (dirty) => set({ isDirty: dirty }),

    resetEditor: () => set(initialState),
}))
